import { useEffect } from 'react';
import { ContentType, decodeCollaborationUpdate, hashNoteText, isCollaborationStateCurrent, MutationType, NoteType, } from '@standardnotes/snjs';
import { renderCollaborationState } from './renderCollaborationState';
import { SuperNotePreviewCharLimit } from '../../SuperEditor';
const needsRender = (note) => {
    var _a;
    return note.noteType === NoteType.Super &&
        Boolean((_a = note.myRadmitClaudeCollaboration) === null || _a === void 0 ? void 0 : _a.textStale) &&
        isCollaborationStateCurrent(note);
};
/**
 * After two versions of a collaborative Super note are merged during sync, only the Yjs state contains
 * the merged content. This renders the note text (and preview) from it, also for notes not open in an editor.
 */
export class CollaborativeNoteTextRenderer {
    constructor(items, mutator, sync) {
        this.items = items;
        this.mutator = mutator;
        this.sync = sync;
        this.disposer = this.items.streamItems(ContentType.TYPES.Note, ({ changed, inserted }) => {
            for (const note of [...changed, ...inserted]) {
                if (needsRender(note)) {
                    void this.render(note.uuid);
                }
            }
        });
    }
    deinit() {
        this.disposer();
    }
    async render(uuid) {
        const note = this.items.findItem(uuid);
        if (!note || !needsRender(note) || !note.myRadmitClaudeCollaboration) {
            return;
        }
        const collaboration = note.myRadmitClaudeCollaboration;
        let rendered;
        try {
            rendered = renderCollaborationState(decodeCollaborationUpdate(collaboration.state), SuperNotePreviewCharLimit);
        }
        catch (error) {
            console.error('Could not render merged note', error);
            return;
        }
        await this.mutator.changeItem(note, (mutator) => {
            var _a;
            /** The note was changed meanwhile (e.g. by an open editor), which already saved a rendered text */
            if (((_a = mutator.getItem().myRadmitClaudeCollaboration) === null || _a === void 0 ? void 0 : _a.state) !== collaboration.state) {
                return;
            }
            mutator.text = rendered.text;
            mutator.preview_plain = rendered.previewPlain;
            mutator.preview_html = undefined;
            mutator.myRadmitClaudeCollaboration = {
                state: collaboration.state,
                origin: collaboration.origin,
                textHash: hashNoteText(rendered.text),
            };
        }, MutationType.NoUpdateUserTimestamps);
        void this.sync.sync();
    }
}
/** Runs the renderer while the application is launched */
export function useCollaborativeNoteTextRenderer(application, launched) {
    useEffect(() => {
        if (!launched) {
            return;
        }
        const renderer = new CollaborativeNoteTextRenderer(application.items, application.mutator, application.sync);
        return () => renderer.deinit();
    }, [application, launched]);
}
